// Import directly from shared types
import { serverUrl } from '@utils/MkEnv'
import { buildNavTree } from '@utils/buildNavTree'
import { toTitleCase } from '@utils/formattingUtils'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { MkConfig, MkNavItem, ELocationType, ILocation, IMkLocationResultList, EMkLocationTypeIcon, FooterItem } from '@typescript'
import { API_CONTENT, API_CONFIGS_BY_GROUPS, API_CONFIG_BY_GROUP, API_CONFIG_GROUPS, API_CONFIGV2_KEY, API_RESORTS_NAMES, API_REGION_LIST, API_LOCATION_MAP } from '@constants'
import { API_SEARCH_FILTERS } from '@search/services/MkSearchConstants'

/**
 * Base function to fetch data from the server.
 * @param url - The URL to fetch.
 * @param headers - The optional headers (should include authentication headers for restricted calls).
 * @returns A promise that resolves to the fetched data.
 */
export async function fetchApi<T>(url: string, headers?: Record<string, string>): Promise<T> {
  try {
    const response = await fetch(url, {
      headers: { ...headers },
    })

    const contentType = response.headers.get('content-type') || ''
    let data: any = null

    // Always read the body text once and decide how to parse it
    const text = await response.text().catch(() => '')
    const trimmed = text ? text.trim() : ''

    if (trimmed) {
      if (contentType.includes('application/json')) {
        try {
          data = JSON.parse(trimmed)
        } catch (parseError) {
          console.warn(`Failed to parse JSON response from ${url}:`, parseError)
          data = null
        }
      } else {
        // Fallback: if it looks like JSON, try to parse anyway
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            data = JSON.parse(trimmed)
          } catch (parseError) {
            // Not JSON; return raw text
            data = trimmed as any
          }
        } else {
          data = trimmed as any
        }
      }
    }

    if (!response.ok) {
      console.warn(`API request failed for ${url}: ${response.status} ${response.statusText}`)
      return null as T
    }

    return data as T
  } catch (error) {
    console.warn(`Error fetching from URL (${url}): ${error instanceof Error ? error.message : String(error)}`)
    return null as T
  }
}

/**
 * Function to pass data to the server with optional authentication headers.
 * @param method - The HTTP method to use (e.g., 'POST', 'PUT').
 * @param url - The URL.
 * @param body - The body of the request.
 * @param headers - The optional headers (should include authentication headers for restricted calls).
 * @returns A promise that resolves to the response data.
 */
export async function performRequest<T>(method: string, url: string, body: string, headers?: Record<string, string>): Promise<T> {
  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body,
    })

    const contentType = response.headers.get('content-type') || ''
    let data: any = null

    // Always read the body text once and decide how to parse it
    const text = await response.text().catch(() => '')
    const trimmed = text ? text.trim() : ''

    if (trimmed) {
      if (contentType.includes('application/json')) {
        try {
          data = JSON.parse(trimmed)
        } catch (parseError) {
          console.warn(`Failed to parse JSON response from ${url}:`, parseError)
          data = null
        }
      } else {
        // Fallback: if it looks like JSON, try to parse anyway
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            data = JSON.parse(trimmed)
          } catch (parseError) {
            // Not JSON; return raw text
            data = trimmed as any
          }
        } else {
          data = trimmed as any
        }
      }
    }

    if (!response.ok) {
      console.warn(`API request failed for ${url}: ${response.status} ${response.statusText}`)
      return null as T
    }

    return data as T
  } catch (error) {
    console.warn(`Error performing request to URL (${url}): ${error instanceof Error ? error.message : String(error)}`)
    return null as T
  }
}

/**
 * Function to post data to the server with optional authentication headers.
 * @param url - The URL to post.
 * @param body - The body of the request.
 * @param headers - The optional headers (should include authentication headers for restricted calls).
 * @returns A promise that resolves to the posted data.
 */
export async function postApi<T>(url: string, body: string, headers?: Record<string, string>): Promise<T> {
  return performRequest('POST', url, body, headers)
}

/**
 * Fetches a list of items of type T from the server.
 * @param url - The URL to fetch.
 * @returns A promise that resolves to an array of T.
 */
async function fetchApiList<T>(url: string): Promise<T[]> {
  try {
    const response = await fetch(url)

    // Check if response has content before trying to parse JSON
    const contentType = response.headers.get('content-type')
    let data: any

    if (contentType && contentType.includes('application/json')) {
      const text = await response.text()
      if (text.trim()) {
        try {
          data = JSON.parse(text)
        } catch (parseError) {
          console.warn(`Failed to parse JSON response from ${url}:`, parseError)
          data = null
        }
      } else {
        data = null
      }
    } else {
      data = null
    }

    if (!response.ok) {
      console.warn(`API request failed for ${url}: ${response.status} ${response.statusText}`)
      return [] as T[]
    }

    // Validate the response is an array
    if (!Array.isArray(data)) {
      console.warn(`Expected an array from ${url}, but received: ${typeof data}`)
      return [] as T[]
    }

    return data as T[]
  } catch (error) {
    console.warn(`Error fetching list from URL (${url}): ${error instanceof Error ? error.message : String(error)}`)
    return [] as T[]
  }
}

/**
 * Fetch content based on kind and slug.
 * @param kind - The type of resource to fetch.
 * @param slug - The slug identifying the resource.
 * @returns A promise that resolves to the fetched data.
 */
export async function fetchContent<T>(kind: string, slug: string): Promise<T> {
  const url = `${serverUrl}${API_CONTENT}/${kind}/${slug}`
  return fetchApi<T>(url)
}

/**
 * Fetch a list of content based on the specified kind.
 * @param kind - The type of resource to fetch.
 * @returns A promise that resolves to an array of the fetched data.
 */
export async function fetchContentList<T>(kind: string): Promise<T[]> {
  const url = `${serverUrl}${API_CONTENT}/${kind}`
  return fetchApiList<T>(url)
}

/**
 * Fetch a list of content based on the specified kind.
 * @param kind - The type of resource to fetch.
 * @param location - The location to filter the list.
 * @returns A promise that resolves to an array of the fetched data.
 */
export async function fetchContentListForLocation<T>(kind: string, location: string): Promise<T[]> {
  const url = `${serverUrl}${API_CONTENT}/${kind}?location=${location}`
  return fetchApiList<T>(url)
}

/**
 * Fetch a list of content based on the specified kind.
 * @param kind - The type of resource to fetch.
 * @param prefix - The prefix to filter the list.
 * @returns A promise that resolves to an array of the fetched data.
 */
export async function fetchContentListMatching<T>(kind: string, prefix: string): Promise<T[]> {
  const url = `${serverUrl}${API_CONTENT}/${kind}?s=${prefix}`
  return fetchApiList<T>(url)
}

/**
 * Fetch configuration based on site and name.
 * @param site - The site identifier for the configuration.
 * @param name - The name of the configuration.
 * @returns A promise that resolves to the fetched data.
 */
export async function fetchConfig<T>(site: string | null, name: string): Promise<T> {
  const url = `${serverUrl}${API_CONFIG_BY_GROUP}`
  const body = `/${site}/${name}`
  return postApi<T>(url, body)
}

/**
 * Fetch configuration based on site and name.
 * @param key - The name of the parent of config tree.
 * @returns A promise that resolves to nested config tree
 */
export async function fetchConfigV2<T>(key: string): Promise<T> {
  const url = `${serverUrl}${API_CONFIGV2_KEY}${key}`
  return fetchApi<T>(url)
}

/**
 * Generates a full config key
 * @param site - The current site context
 * @param subPath - The specific subpath for the config key
 * @returns Full config key
 */
export function configKey(site: string, subPath: string): string {
  return `/${site}/${subPath}`
}

/**
 * Fetch a list of configurations based on an array of group keys.
 * @param groupKeys - An array of group keys to fetch.
 * @returns A promise that resolves to an array of the fetched data.
 */
export async function fetchConfigsByGroupKeys<T>(site: string, groupKeys: string[]): Promise<T> {
  const url = `${serverUrl}${API_CONFIGS_BY_GROUPS}`
  const body = JSON.stringify(groupKeys)
  return postApi<T>(url, body)
}

/**
 * Fetch configuration based on site and group.
 * @param site - The site identifier for the configuration.
 * @param group - The group of the configuration.
 * @returns A promise that resolves to the fetched data.
 */
export async function fetchConfigList<T>(paths: string[]): Promise<T[]> {
  const url = `${serverUrl}${API_CONFIGS_BY_GROUPS}`
  const body = JSON.stringify(paths)
  return postApi<T[]>(url, body)
}

/**
 * Fetches a list of configuration groups from the server, filtered by the specified query parameter.
 *
 * @param {string} filterBy - The query parameter to filter the configuration groups.
 * @returns {Promise<string[]>} A promise that resolves to an array of configuration group keys.
 */
export async function fetchConfigGroups(filterBy: string): Promise<string[]> {
  const url = `${serverUrl}${API_CONFIG_GROUPS}?filterBy=${encodeURIComponent(filterBy)}`
  return fetchApiList<string>(url)
}

export function configToMenuModel(node: any): MkNavItem[] {
  return node.children.map((child: any) => ({
    label: child.key, // TODO: handle key/values properly (pre-demo kludge)
    url: child.value,
    items: child.children.length > 0 ? configToMenuModel(child) : undefined,
  }))
}

/**
 * Fetch navigation configuration based on site and navigation group.
 * @param site - The site identifier for the configuration.
 * @param navGroup - The navigation group of the configuration.
 * @returns A promise that resolves to the fetched data formatted as navigation tree.
 */
export async function fetchNav(site: string, navGroup: string): Promise<MkNavItem[]> {
  const groups = await fetchConfigGroups(`/${site}/${navGroup}`)
  const navItems = await fetchConfigsByGroupKeys(site, groups)
  return buildNavTree(site, navItems as MkNavItem[])
}

/**
 * Transform header configuration data into MkTopNavProps format.
 * @param items - The configuration data to transform.
 * @returns A transformed MkTopNavProps object.
 */
export function transformHeaderConfigs<MkTopNavProps>(items: MkConfig[]): MkTopNavProps {
  const headerConfigs: Record<string, any> = {}
  items.forEach((item) => {
    if (item.value === 'true') {
      headerConfigs[item.key] = true
    } else if (item.value === 'false') {
      headerConfigs[item.key] = false
    } else {
      headerConfigs[item.key] = item.value
    }
  })
  return headerConfigs as MkTopNavProps
}

/**
 * Asynchronous function to iterate through each AppFooter config group, fetch its items from the API, and then map those items to a FooterItem.
 *
 * @param configGroups {string[]} The array of config groups to fetch
 * @returns {FooterItem[]} The array of footer items created from the config groups.
 */
export function mapConfigGroupsToFooterItems(configGroups: string[]): Promise<FooterItem[]> {
  const footerItems: Promise<FooterItem[]> = Promise.all(
    configGroups.map(async (group: string) => {
      let footerItem = {} as FooterItem
      // retrieve the list of KVP objects for each group
      await fetchConfigList([group]).then((data: any) => {
        const { items } = data[0]
        // if the group isn't empty, map it to a FooterItem
        if (items.length > 0) {
          footerItem = {
            label: items[0].value,
            link: items[1].value,
            target: items[2].value,
          }
        }
      })
      return footerItem
    }),
  ) // end of Promise.all

  return footerItems
}

/**
 * Mock search autocomplete data take a query and return a list of suggestions
 * @param query - The search query.
 * @returns A promise that resolves to an array of search suggestions.
 * @example
 * fetchSearchSuggestions("foo").then((suggestions) => {
 *    console.log(suggestions);
 * });
 */
export async function fetchSearchSuggestions(query: string): Promise<any[]> {
  // Changed MkResortName to any[]
  return new Promise((resolve) => {
    const url = `${serverUrl}${API_RESORTS_NAMES}?pattern=${query}`
    fetchApi<any[]>(url).then((resorts) => {
      // Changed MkResortName[] to any[]
      resolve(resorts)
    })
  })
}
/**
 * Fetches the location map from the server or returns cached results.
 * @param brand - Optional brand parameter to filter the location map.
 * @returns A promise that resolves to the location map.
 */
export async function fetchLocationMap<T>(brand?: string): Promise<T> {
  try {
    // Check if location map exists in session storage
    const storageKey = `locationMap${brand ? `_${brand}` : ''}`
    const storedLocationMap = DataStore.getItem<T>(storageKey as EMkStorageKeys)

    if (storedLocationMap) {
      return storedLocationMap
    }

    // If not in session, make the API call
    const url = `${serverUrl}${API_LOCATION_MAP}${brand ? `?brand=${encodeURIComponent(brand)}` : ''}`
    const results = await fetchApi<T>(url)

    // Store in session storage
    DataStore.setItem(storageKey as EMkStorageKeys, results)

    return results
  } catch (error) {
    throw new Error(`Error fetching location map: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * Searches the locationMap for a matching region and returns an array of all the resorts under that region.
 * @param brand club brand
 * @param regionName - The name of the region to search for.
 * @returns A promise that resolves to an array of resorts under the specified region.
 */
export async function fetchResortsByRegion(brand: string, regionName: string): Promise<ILocation[]> {
  const locationMap = await fetchLocationMap<IMkLocationResultList>(brand)

  const resorts: ILocation[] = []

  locationMap.locations.forEach((continent) => {
    continent.countries.forEach((country) => {
      country.regions.forEach((region) => {
        if (region.name.toLowerCase() === regionName.toLowerCase()) {
          region.states.forEach((state) => {
            state.cities.forEach((city) => {
              city.resorts.forEach((resort) => {
                resorts.push({
                  id: resort.locationPath,
                  name: toTitleCase(resort.resortName),
                  title: toTitleCase(resort.resortName),
                  subtitle: toTitleCase(`${city.name}, ${state.name}`),
                  locationType: ELocationType.RESORT,
                  resortName: resort.resortName,
                  resortId: resort.resortId,
                  icon: EMkLocationTypeIcon.RESORT,
                })
              })
            })
          })
        }
      })
    })
  })

  return resorts
}

/**
 * Fetch explore results based on the specified query.
 * @param brand club brand
 * @param query - The query to fetch explore results for.
 * @returns A promise that resolves to an array of explore results.
 */
export async function fetchLocationResults(brand: string, query: string): Promise<ILocation[]> {
  const locationMap = await fetchLocationMap<IMkLocationResultList>(brand)

  return new Promise((resolve) => {
    const regions: ILocation[] = []
    const states: ILocation[] = []
    const cities: ILocation[] = []
    const resorts: ILocation[] = []

    locationMap.locations.forEach((continent: { countries: any[] }) => {
      continent.countries.forEach((country) => {
        if (country.name.toLowerCase().includes(query.toLowerCase())) {
          country.regions.forEach((region: { states: any[]; name: any }) => {
            region.states.forEach((state) => {
              state.cities.forEach((city: { resorts: any[]; name: any }) => {
                city.resorts.forEach((resort) => {
                  resorts.push({
                    id: resort.locationPath,
                    name: toTitleCase(resort.resortName),
                    title: toTitleCase(resort.resortName),
                    subtitle: toTitleCase(`${city.name}, ${state.name}`),
                    locationType: ELocationType.RESORT,
                    resortName: resort.resortName,
                    resortId: resort.resortId,
                    icon: EMkLocationTypeIcon.RESORT,
                  })
                })
              })
            })
          })
        }

        country.regions.forEach((region) => {
          if (region.name.toLowerCase().includes(query.toLowerCase())) {
            regions.push({
              id: region.locationPath,
              name: toTitleCase(region.name),
              title: toTitleCase(region.name),
              subtitle: toTitleCase(country.name),
              locationType: ELocationType.REGION,
              icon: EMkLocationTypeIcon.REGION,
            })

            region.states.forEach((state) => {
              state.cities.forEach((city: { resorts: any[]; name: any }) => {
                city.resorts.forEach((resort) => {
                  resorts.find((r) => r.id === resort.locationPath) ||
                    resorts.push({
                      id: resort.locationPath,
                      name: toTitleCase(resort.resortName),
                      title: toTitleCase(resort.resortName),
                      subtitle: toTitleCase(`${city.name}, ${state.name}`),
                      locationType: ELocationType.RESORT,
                      resortName: resort.resortName,
                      resortId: resort.resortId,
                      icon: EMkLocationTypeIcon.RESORT,
                    })
                })
              })
            })
          }

          region.states.forEach((state) => {
            if (state.name.toLowerCase().includes(query.toLowerCase())) {
              states.find((r) => r.id.includes(state.name.toLowerCase())) ||
                states.push({
                  id: state.locationPath,
                  name: toTitleCase(state.name),
                  title: toTitleCase(state.name),
                  subtitle: toTitleCase(country.name),
                  locationType: ELocationType.STATE,
                  icon: EMkLocationTypeIcon.STATE,
                })

              state.cities.forEach((city: { resorts: any[]; name: any }) => {
                city.resorts.forEach((resort) => {
                  resorts.find((r) => r.id === resort.locationPath) ||
                    resorts.push({
                      id: resort.locationPath,
                      name: toTitleCase(resort.resortName),
                      title: toTitleCase(resort.resortName),
                      subtitle: toTitleCase(`${city.name}, ${state.name}`),
                      locationType: ELocationType.RESORT,
                      resortName: resort.resortName,
                      resortId: resort.resortId,
                      icon: EMkLocationTypeIcon.RESORT,
                    })
                })
              })
            }

            state.cities.forEach((city) => {
              if (city.name.toLowerCase().includes(query.toLowerCase())) {
                cities.push({
                  id: city.locationPath,
                  name: toTitleCase(city.name),
                  title: toTitleCase(city.name),
                  subtitle: toTitleCase(`${state.name}, ${country.name}`),
                  locationType: ELocationType.CITY,
                  icon: EMkLocationTypeIcon.CITY,
                })
                city.resorts.forEach((resort) => {
                  resorts.find((r) => r.id === resort.locationPath) ||
                    resorts.push({
                      id: resort.locationPath,
                      name: toTitleCase(resort.resortName),
                      title: toTitleCase(resort.resortName),
                      subtitle: toTitleCase(`${city.name}, ${state.name}`),
                      locationType: ELocationType.RESORT,
                      resortName: resort.resortName,
                      resortId: resort.resortId,
                      icon: EMkLocationTypeIcon.RESORT,
                    })
                })
              }

              city.resorts.forEach((resort) => {
                if (resort.resortName.toLowerCase().includes(query.toLowerCase())) {
                  resorts.find((r) => r.id === resort.locationPath) ||
                    resorts.push({
                      id: resort.locationPath,
                      name: toTitleCase(resort.resortName),
                      title: toTitleCase(resort.resortName),
                      subtitle: toTitleCase(`${city.name}, ${state.name}`),
                      locationType: ELocationType.RESORT,
                      resortName: resort.resortName,
                      resortId: resort.resortId,
                      icon: EMkLocationTypeIcon.RESORT,
                    })
                }
              })
            })
          })
        })
      })
    })
    resolve(regions.concat(states, cities, resorts))
  })
}

/**
 * Fetch a list of regions based on the specified brand.
 * @param brand - The brand to filter the regions.
 * @returns A promise that resolves to an array of regions.
 */
export async function fetchRegionList<T>(brand: string): Promise<T[]> {
  return new Promise((resolve) => {
    const url = `${serverUrl}${API_REGION_LIST}?brand=${brand}`
    fetchApiList<T>(url).then((regions) => {
      resolve(regions)
    })
  })
}

/**
 * Fetch filters for the resort search.
 * This function retrieves the default filters before a search is performed.
 *
 * @param headers - Authentication headers needed for the API call
 * @param locationPath - The location path to filter results (e.g. resort ID)
 * @returns A promise that resolves to the filter data
 */
export async function fetchFilters<T>(headers: Record<string, string>, locationPath?: string): Promise<T> {
  let url = `${serverUrl}${API_SEARCH_FILTERS}`

  // Add location parameter if provided
  if (locationPath) {
    url = `${url}?locationPath=${encodeURIComponent(locationPath)}`
  }

  return fetchApi<T>(url, headers)
}
